import {
  keepPreviousData,
  queryOptions,
  useQuery,
  useSuspenseQuery,
  type UseQueryResult,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query'

import { hasCode } from '@/api/errors'
import { signalsKeys } from '@/features/signals/api/keys'
import { signalsService } from '@/features/signals/api/signalsService'
import { useMarketStatus } from '@/hooks/useMarketStatus'
import type {
  BreakoutsQuery,
  ClosedTrade,
  CycleMap,
  CycleMapQuery,
  HistoryQuery,
  LevelBoard,
  LiveRun,
  MomentumRow,
  OpenTrade,
  SignalBoard,
  SignalDashboard,
  SignalPerformance,
  SignalSetup,
  SignalTechnicals,
  SignalsList,
  SignalsPage,
  SignalsPaging,
  SteadyQuery,
  SteadyRow,
  SwingRow,
  TrendQuery,
  VolatileQuery,
  TrendRow,
} from '@/schemas/signals'

/**
 * Signal Zone's reads: one `queryOptions` factory per endpoint (key + fetch,
 * and the freshness the data allows — what a route loader hands
 * `ensureRouteData`/`prefetchRouteData`), and hooks that spread them.
 *
 * Read with `keepPreviousData` wherever the key carries something the member
 * changes (a session, a page, a search), so a change holds the old board in
 * place under RefreshRegion's refresh line rather than dropping to a skeleton
 * (docs/route-loading.md, "Search params"). The one read keyed on nothing
 * (Open trades) is suspense: its loader always has it.
 */

/**
 * mm-be caches every signals read for five minutes (`SIGNALS_CACHE_TTL`) and
 * the after-close run lands once a session, so asking sooner only re-reads
 * mm-be's own cache.
 */
const SIGNALS_STALE_MS = 5 * 60_000

/**
 * Live's poll while NEPSE trades. The intraday job runs every ten minutes
 * behind that same five-minute cache, so a minute is already four times
 * fresher than the data can move — and nothing polls while the market is shut.
 */
export const SIGNALS_LIVE_POLL_MS = 60_000

/** How many sessions a picker offers: mm-be's default, about six weeks of trading. */
export const SESSION_PICKER_LIMIT = 30

// ── Sessions ─────────────────────────────────────────────────────────────────

export const signalSessionsQueryOptions = () =>
  queryOptions<string[]>({
    queryKey: signalsKeys.sessions(SESSION_PICKER_LIMIT),
    queryFn: () => signalsService.sessions(SESSION_PICKER_LIMIT),
    staleTime: SIGNALS_STALE_MS,
  })

/** The session picker's options. Secondary everywhere: the picker draws without them. */
export const useSignalSessions = (): UseQueryResult<string[]> =>
  useQuery(signalSessionsQueryOptions())

// ── Dashboard, Daily, Live ───────────────────────────────────────────────────

export const signalDashboardQueryOptions = (date: string | null) =>
  queryOptions<SignalDashboard | null>({
    queryKey: signalsKeys.dashboard(date),
    queryFn: () => signalsService.dashboard(date),
    staleTime: SIGNALS_STALE_MS,
  })

export const useSignalDashboard = (date: string | null): UseQueryResult<SignalDashboard | null> =>
  useQuery({ ...signalDashboardQueryOptions(date), placeholderData: keepPreviousData })

export const dailySignalsQueryOptions = (date: string | null) =>
  queryOptions<SignalBoard | null>({
    queryKey: signalsKeys.daily(date),
    queryFn: () => signalsService.daily(date),
    staleTime: SIGNALS_STALE_MS,
  })

export const useDailySignals = (date: string | null): UseQueryResult<SignalBoard | null> =>
  useQuery({ ...dailySignalsQueryOptions(date), placeholderData: keepPreviousData })

export const liveSignalsQueryOptions = (run: number | null) =>
  queryOptions<SignalBoard | null>({
    queryKey: signalsKeys.live(run),
    queryFn: () => signalsService.live(run),
    staleTime: SIGNALS_LIVE_POLL_MS,
  })

/**
 * The session in progress, re-read every minute while the market is open and
 * not at all once it shuts. A poll re-renders the boards in place; it never
 * suspends (the data is already there) and never replays their entrance.
 *
 * Only the all-runs view (`run` null) polls: a picked run is a past picture
 * that cannot change. Picking one keeps the old board under RefreshRegion's
 * refresh line (`keepPreviousData`) rather than dropping to a skeleton, so it
 * is a plain query, not suspense; the loader still has the entry URL's read.
 */
export const useLiveSignals = (run: number | null): UseQueryResult<SignalBoard | null> => {
  const { isOpen } = useMarketStatus()
  return useQuery({
    ...liveSignalsQueryOptions(run),
    placeholderData: keepPreviousData,
    refetchInterval: run === null && isOpen ? SIGNALS_LIVE_POLL_MS : false,
  })
}

export const liveRunsQueryOptions = () =>
  queryOptions<LiveRun[]>({
    queryKey: signalsKeys.liveRuns(),
    queryFn: () => signalsService.liveRuns(),
    staleTime: SIGNALS_LIVE_POLL_MS,
  })

/**
 * The run picker's options, re-read with the board while NEPSE trades so a new
 * run appears. A SECONDARY read: the picker draws "All signals" without it.
 */
export const useLiveRuns = (): UseQueryResult<LiveRun[]> => {
  const { isOpen } = useMarketStatus()
  return useQuery({
    ...liveRunsQueryOptions(),
    refetchInterval: isOpen ? SIGNALS_LIVE_POLL_MS : false,
  })
}

// ── Trend, Breakouts ─────────────────────────────────────────────────────────

export const trendSignalsQueryOptions = (query: TrendQuery) =>
  queryOptions<SignalsPage<TrendRow>>({
    queryKey: signalsKeys.trend(query),
    queryFn: () => signalsService.trend(query),
    staleTime: SIGNALS_STALE_MS,
  })

export const useTrendSignals = (query: TrendQuery): UseQueryResult<SignalsPage<TrendRow>> =>
  useQuery({ ...trendSignalsQueryOptions(query), placeholderData: keepPreviousData })

export const breakoutsQueryOptions = (query: BreakoutsQuery) =>
  queryOptions<LevelBoard | null>({
    queryKey: signalsKeys.breakouts(query),
    queryFn: () => signalsService.breakouts(query),
    staleTime: SIGNALS_STALE_MS,
  })

export const useBreakouts = (query: BreakoutsQuery): UseQueryResult<LevelBoard | null> =>
  useQuery({ ...breakoutsQueryOptions(query), placeholderData: keepPreviousData })

// ── Volatile, Essentials ─────────────────────────────────────────────────────

export const volatileQueryOptions = (query: VolatileQuery) =>
  queryOptions<SignalsPage<SwingRow>>({
    queryKey: signalsKeys.volatile(query),
    queryFn: () => signalsService.volatile(query),
    staleTime: SIGNALS_STALE_MS,
  })

export const useVolatileStocks = (query: VolatileQuery): UseQueryResult<SignalsPage<SwingRow>> =>
  useQuery({ ...volatileQueryOptions(query), placeholderData: keepPreviousData })

export const steadyQueryOptions = (query: SteadyQuery) =>
  queryOptions<SignalsPage<SteadyRow>>({
    queryKey: signalsKeys.steady(query),
    queryFn: () => signalsService.steady(query),
    staleTime: SIGNALS_STALE_MS,
  })

export const useSteadyStocks = (query: SteadyQuery): UseQueryResult<SignalsPage<SteadyRow>> =>
  useQuery({ ...steadyQueryOptions(query), placeholderData: keepPreviousData })

export const momentumQueryOptions = (paging: SignalsPaging) =>
  queryOptions<SignalsPage<MomentumRow>>({
    queryKey: signalsKeys.momentum(paging),
    queryFn: () => signalsService.momentum(paging),
    staleTime: SIGNALS_STALE_MS,
  })

export const useMomentumMovers = (
  paging: SignalsPaging,
): UseQueryResult<SignalsPage<MomentumRow>> =>
  useQuery({ ...momentumQueryOptions(paging), placeholderData: keepPreviousData })

// ── Open trades, History, Cycle map ──────────────────────────────────────────

export const openTradesQueryOptions = () =>
  queryOptions<SignalsList<OpenTrade>>({
    queryKey: signalsKeys.openTrades(),
    queryFn: () => signalsService.openTrades(),
    staleTime: SIGNALS_STALE_MS,
  })

export const useOpenTrades = (): UseSuspenseQueryResult<SignalsList<OpenTrade>> =>
  useSuspenseQuery(openTradesQueryOptions())

export const signalHistoryQueryOptions = (query: HistoryQuery) =>
  queryOptions<SignalsPage<ClosedTrade>>({
    queryKey: signalsKeys.history(query),
    queryFn: () => signalsService.history(query),
    staleTime: SIGNALS_STALE_MS,
  })

export const useSignalHistory = (query: HistoryQuery): UseQueryResult<SignalsPage<ClosedTrade>> =>
  useQuery({ ...signalHistoryQueryOptions(query), placeholderData: keepPreviousData })

export const cycleMapQueryOptions = (query: CycleMapQuery) =>
  queryOptions<CycleMap | null>({
    queryKey: signalsKeys.cycleMap(query),
    queryFn: () => signalsService.cycleMap(query),
    staleTime: SIGNALS_STALE_MS,
  })

export const useCycleMap = (query: CycleMapQuery): UseQueryResult<CycleMap | null> =>
  useQuery({ ...cycleMapQueryOptions(query), placeholderData: keepPreviousData })

// ── One stock: the Signal page and its Technicals ────────────────────────────

/** mm-be's 404 for a symbol it does not list, on either per-stock read. */
const SYMBOL_NOT_FOUND = 'SIGNALS_SYMBOL_NOT_FOUND'

/**
 * The read, with mm-be's not-found as `null`: the page's not-found state, a
 * normal answer, never the route's error page with a "Try again" that would
 * get the same 404.
 */
async function nullOnSymbolNotFound<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read()
  } catch (error) {
    if (hasCode(error, SYMBOL_NOT_FOUND)) return null
    throw error
  }
}

export const signalPerformanceQueryOptions = (symbol: string) =>
  queryOptions<SignalPerformance | null>({
    queryKey: signalsKeys.performance(symbol),
    queryFn: () => nullOnSymbolNotFound(() => signalsService.performance(symbol)),
    staleTime: SIGNALS_STALE_MS,
  })

/**
 * The Signal page's main read, keyed on the path's symbol alone: its loader
 * always has it. `null` is a symbol mm-be does not list.
 */
export const useSignalPerformance = (
  symbol: string,
): UseSuspenseQueryResult<SignalPerformance | null> =>
  useSuspenseQuery(signalPerformanceQueryOptions(symbol))

export const signalTechnicalsQueryOptions = (symbol: string) =>
  queryOptions<SignalTechnicals | null>({
    queryKey: signalsKeys.technicals(symbol),
    queryFn: () => nullOnSymbolNotFound(() => signalsService.technicals(symbol)),
    staleTime: SIGNALS_STALE_MS,
  })

/** The Technicals page's main read, as `useSignalPerformance` is the Signal page's. */
export const useSignalTechnicals = (
  symbol: string,
): UseSuspenseQueryResult<SignalTechnicals | null> =>
  useSuspenseQuery(signalTechnicalsQueryOptions(symbol))

/** The chart rail's read of a stock's open setup; `null` is a symbol mm-be does not list. */
export const signalSetupQueryOptions = (symbol: string) =>
  queryOptions<SignalSetup | null>({
    queryKey: signalsKeys.setup(symbol),
    queryFn: () => nullOnSymbolNotFound(() => signalsService.setup(symbol)),
    staleTime: SIGNALS_STALE_MS,
  })

// ── Index symbols ────────────────────────────────────────────────────────────

/** The index list moves when NEPSE adds a sector, not within a session. */
export const indexSymbolsQueryOptions = () =>
  queryOptions<string[]>({
    queryKey: signalsKeys.indexSymbols(),
    queryFn: ({ client }) => signalsService.indexSymbols(client),
    staleTime: SIGNALS_STALE_MS,
  })

const toSymbolSet = (symbols: string[]): ReadonlySet<string> => new Set(symbols)
const NO_SYMBOLS: ReadonlySet<string> = new Set()

/**
 * The symbols in a signal list that are an index or a sector sub-index, not a
 * stock (`NEPSE`, `MANUFACTURE`): `/stocks/NEPSE` is not a stock page, the
 * rule search, the watchlist and the transaction form already keep
 * (`features/search/api/searchService.ts`). Empty until the list lands, or if
 * it fails, so every row stays a link as before rather than none.
 */
export const useIndexSymbols = (): ReadonlySet<string> =>
  useQuery({ ...indexSymbolsQueryOptions(), select: toSymbolSet }).data ?? NO_SYMBOLS
